import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useUserSettings } from '@/hooks/useUserSettings'
import { useSavingGoalsStore } from '@/store/savingGoalsStore'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { 
  Building2,
  Plus,
  Target,
  Calendar,
  MoreVertical,
  Edit,
  Trash,
  CheckCircle,
  Pause,
  Play,
  List,
  Search,
  Filter,
  FileText
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { AccountType } from '@/types/account'
import { useAccountsStore } from '@/store/accountsStore'
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { AddSavingGoalDialog } from '@/components/saving-goals/AddSavingGoalDialog'
import { EditSavingGoalDialog } from './EditSavingGoalDialog'
import { TransactionsDialog } from './TransactionsDialog'
import AssignTransactionsDialog from './AssignTransactionsDialog'

// Define additional properties for SavingGoal that might be used in this component
interface ExtendedSavingGoal {
  id: string;
  user_id: string;
  name: string;
  target_amount: number;
  current_amount: number;
  currency: string;
  target_date: string | null;
  icon: string | null;
  color: string | null;
  description: string | null; // Using description from database schema
  account_id: string; 
  is_completed: boolean;
  status?: 'active' | 'paused' | 'completed';
  linkedTransactions?: any[];
  created_at: string;
  updated_at: string;
  // Account information
  account?: {
    id: string;
    name: string;
    type: AccountType;
    balance: number;
    currency?: string;
  };
}

// Interface for our simplified account type
interface SimplifiedAccount {
  id: string;
  name: string;
  type: AccountType;
  balance: number;
  currency?: string;
}

export function SavingGoalsList() {
  const { user } = useAuth()
  const { settings } = useUserSettings()
  const { goals: rawGoals, isLoading, error, fetchGoals, updateGoal, deleteGoal } = useSavingGoalsStore()
  const { accounts, loading: accountsLoading, fetchAccounts } = useAccountsStore()
  
  // We need to type-cast the SavingGoal[] to ExtendedSavingGoal[] since we know the actual schema uses 'description' 
  // but our ExtendedSavingGoal interface also uses 'description'
  const goalsWithoutAccounts = rawGoals as any as ExtendedSavingGoal[];
  
  // Add account information to each goal
  const goals = goalsWithoutAccounts.map(goal => {
    const account = accounts.find(acc => acc.id === goal.account_id)
    return {
      ...goal,
      account: account ? {
        id: account.id,
        name: account.name,
        type: account.type as AccountType,
        balance: account.balance, // Server-calculated balance from account_balances_view
        currency: account.currency
      } : undefined
    }
  })
  
  // Group goals by account
  const goalsByAccount = goals.reduce((acc, goal) => {
    if (!goal.account_id) return acc;
    
    if (!acc[goal.account_id]) {
      acc[goal.account_id] = [];
    }
    
    acc[goal.account_id].push(goal);
    return acc;
  }, {} as Record<string, ExtendedSavingGoal[]>);
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState<ExtendedSavingGoal | null>(null)
  const [viewingTransactions, setViewingTransactions] = useState<ExtendedSavingGoal | null>(null)
  const [assigningTransactionsToGoal, setAssigningTransactionsToGoal] = useState<ExtendedSavingGoal | null>(null)

  useEffect(() => {
    if (user) {
      fetchGoals()
      fetchAccounts(user.id)
    }
  }, [user, fetchGoals, fetchAccounts])

  useEffect(() => {
    return () => {
      setIsAddDialogOpen(false)
      setEditingGoal(null)
      setViewingTransactions(null)
      setAssigningTransactionsToGoal(null)
    }
  }, [])

  const handleDialogClose = () => {
    setEditingGoal(null)
    setViewingTransactions(null)
    setIsAddDialogOpen(false)
    setAssigningTransactionsToGoal(null)
    setTimeout(() => {
      document.body.style.pointerEvents = 'auto'
    }, 100)
  }

  const handleStatusChange = async (goal: ExtendedSavingGoal, newStatus: 'active' | 'paused' | 'completed') => {
    if (!user) return

    try {
      await updateGoal(goal.id, {
        status: newStatus
      })
      
      toast.success("Saving goal updated successfully");
    } catch (error) {
      console.error('Error updating goal status:', error)
      toast.error("Failed to update saving goal");
    }
  }

  const handleDeleteGoal = async (goalId: string) => {
    if (!user) return
    
    if (!window.confirm("Are you sure you want to delete this saving goal?")) {
      return
    }

    try {
      await deleteGoal(goalId)
      toast.success("Saving goal deleted successfully");
    } catch (error) {
      console.error('Error deleting goal:', error)
      toast.error("Failed to delete saving goal");
    }
  }

  // Helper for formatting currency amounts - moved inside the component to use the settings
  const formatAmount = (amount: number, currency?: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }
  
  // Helper function to calculate time left
  const calculateTimeLeft = (deadline: Date | string): string => {
    const deadlineDate = deadline instanceof Date ? deadline : new Date(deadline);
    const now = new Date();
    
    if (isNaN(deadlineDate.getTime())) return 'No deadline';
    
    if (deadlineDate < now) return 'Overdue';
    
    const diffTime = deadlineDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day left';
    if (diffDays < 30) return `${diffDays} days left`;
    
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return '1 month left';
    if (diffMonths < 12) return `${diffMonths} months left`;
    
    const diffYears = Math.floor(diffMonths / 12);
    if (diffYears === 1) return '1 year left';
    return `${diffYears} years left`;
  };

  if (isLoading || accountsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-secondary dark:text-primary-dark" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center p-6 bg-negative/10 dark:bg-negative-dark/10 rounded-xl">
        <p className="text-negative dark:text-negative-dark">Failed to load saving goals</p>
        <Button 
          variant="outline" 
          className="mt-4 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-border dark:border-border-dark rounded-xl shadow-sm" 
          onClick={() => fetchGoals()}
        >
          Try again
        </Button>
      </div>
    )
  }

  // Filter for savings accounts only
  const savingsAccounts = accounts.filter(account => account.type === 'savings' && !account.isArchived);
  
  return (
    <div className="space-y-6">
      {/* Action Buttons and Search Bar */}
      <div className="border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 order-2 sm:order-1 sm:flex-nowrap">
            <Button
              variant="outline"
              size="default"
              className="flex-1 sm:flex-none bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm h-11"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
            <Button
              variant="default"
              size="default"
              onClick={() => setIsAddDialogOpen(true)}
              disabled={savingsAccounts.length === 0}
              className="flex-1 sm:flex-none shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90 h-11"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Goal
            </Button>
          </div>
          
          {/* Search Bar */}
          <div className="relative flex-1 order-1 sm:order-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
            <Input
              placeholder="Search goals..."
              className="pl-10 h-11 w-full bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl shadow-sm text-sm"
            />
          </div>
        </div>
      </div>

      {/* Show account-based goals */}
      {Object.keys(goalsByAccount).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(goalsByAccount).map(([accountId, accountGoals]) => {
            const account = accounts.find(acc => acc.id === accountId);
            if (!account) return null;
            
            return (
              <div key={accountId} className="border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 rounded-2xl shadow-sm p-4 sm:p-6">
                {/* Account header */}
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border dark:border-border-dark">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 dark:bg-primary-dark/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-secondary dark:text-primary-dark" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                      {account.name}
                    </h3>
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      {account.currency ? `${account.currency} • ` : ''}Balance: {formatAmount(account.balance, account.currency)}
                    </p>
                  </div>
                </div>
                
                {/* Goals grid for this account */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {accountGoals.map((goal) => {
                    const progress = (goal.current_amount / goal.target_amount) * 100;
                    const displayProgress = Math.min(Math.round(progress * 10) / 10, 100);
                    const deadline = goal.target_date ? new Date(goal.target_date) : new Date();
                    const timeLeft = goal.target_date ? calculateTimeLeft(deadline) : 'No deadline';
                    const isOverdue = timeLeft === 'Overdue';
                    const status = goal.status || 'active';

                    return (
                      <div
                        key={goal.id}
                        className="group bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 space-y-6 hover:border-secondary/50 dark:hover:border-primary-dark/50"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-4 mb-3">
                              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary/20 to-secondary/10 dark:from-primary-dark/20 dark:to-primary-dark/10 flex items-center justify-center shadow-inner">
                                <Target className="w-6 h-6 text-secondary dark:text-primary-dark" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary truncate bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                                  {goal.name}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className={cn(
                                    "rounded-lg font-medium px-3 py-1 shadow-sm",
                                    status === 'active' && "bg-[#22C55E] text-white dark:bg-[#22C55E] dark:text-white",
                                    status === 'paused' && "bg-[#EF4444] text-white dark:bg-[#EF4444] dark:text-white",
                                    status === 'completed' && "bg-[#EF4444] text-white dark:bg-[#EF4444] dark:text-white"
                                  )}>
                                    <div className="flex items-center gap-1.5">
                                      {status === 'active' && <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
                                      {status.charAt(0).toUpperCase() + status.slice(1)}
                                    </div>
                                  </Badge>
                                  <span className={cn(
                                    "text-sm",
                                    isOverdue ? "text-negative dark:text-negative-dark" : "text-text-secondary dark:text-text-dark-secondary"
                                  )}>
                                    {timeLeft}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MoreVertical className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 rounded-xl border border-border dark:border-border-dark">
                              <DropdownMenuItem
                                onClick={() => setEditingGoal(goal)}
                                className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary hover:text-secondary hover:bg-secondary/10 dark:hover:text-primary-dark dark:hover:bg-primary-dark/10 rounded-lg"
                              >
                                <Edit className="w-4 h-4" />
                                Edit Goal
                              </DropdownMenuItem>
                              
                              {status !== 'active' && (
                                <DropdownMenuItem
                                  onClick={() => handleStatusChange(goal, 'active')}
                                  className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary hover:text-secondary hover:bg-secondary/10 dark:hover:text-primary-dark dark:hover:bg-primary-dark/10 rounded-lg"
                                >
                                  <Play className="w-4 h-4" />
                                  Activate
                                </DropdownMenuItem>
                              )}
                              
                              {status !== 'paused' && status !== 'completed' && (
                                <DropdownMenuItem
                                  onClick={() => handleStatusChange(goal, 'paused')}
                                  className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary hover:text-secondary hover:bg-secondary/10 dark:hover:text-primary-dark dark:hover:bg-primary-dark/10 rounded-lg"
                                >
                                  <Pause className="w-4 h-4" />
                                  Pause
                                </DropdownMenuItem>
                              )}
                              
                              {status !== 'completed' && (
                                <DropdownMenuItem
                                  onClick={() => handleStatusChange(goal, 'completed')}
                                  className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary hover:text-secondary hover:bg-secondary/10 dark:hover:text-primary-dark dark:hover:bg-primary-dark/10 rounded-lg"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Mark as Complete
                                </DropdownMenuItem>
                              )}
                              
                              <DropdownMenuItem
                                onClick={() => setViewingTransactions(goal)}
                                className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary hover:text-secondary hover:bg-secondary/10 dark:hover:text-primary-dark dark:hover:bg-primary-dark/10 rounded-lg"
                              >
                                <List className="w-4 h-4" />
                                View Transactions
                              </DropdownMenuItem>
                              
                              <DropdownMenuItem
                                onClick={() => setAssigningTransactionsToGoal(goal)}
                                className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary hover:text-secondary hover:bg-secondary/10 dark:hover:text-primary-dark dark:hover:bg-primary-dark/10 rounded-lg"
                              >
                                <FileText className="w-4 h-4" />
                                Assign Transactions
                              </DropdownMenuItem>
                              
                              <DropdownMenuSeparator className="bg-border dark:bg-border-dark" />
                              
                              <DropdownMenuItem
                                onClick={() => handleDeleteGoal(goal.id)}
                                className="flex items-center gap-2 text-negative dark:text-negative-dark hover:bg-negative/10 dark:hover:bg-negative-dark/10 rounded-lg"
                              >
                                <Trash className="w-4 h-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <div className="space-y-6">
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                                Current
                              </p>
                              <p className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                                {formatAmount(goal.current_amount, goal.currency)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                                Target
                              </p>
                              <p className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                                {formatAmount(goal.target_amount, goal.currency)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className={cn(
                                "font-medium",
                                isOverdue ? "text-negative dark:text-negative-dark" : "text-text-secondary dark:text-text-dark-secondary"
                              )}>
                                {displayProgress}% Complete
                              </span>
                              {goal.target_date && (
                                <span className="flex items-center gap-1.5 text-text-secondary dark:text-text-dark-secondary">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(goal.target_date).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <div className="w-full bg-background-input dark:bg-background-dark-input rounded-full h-3 relative shadow-inner overflow-hidden border border-border dark:border-border-dark">
                              <div 
                                className={cn(
                                  "absolute top-0 left-0 h-full rounded-full transition-all duration-300 ease-out",
                                  "bg-gradient-to-r from-secondary to-secondary/90 dark:from-primary-dark dark:to-primary-dark/90",
                                  "shadow-[0_0_10px_rgba(67,148,118,0.3)] dark:shadow-[0_0_10px_rgba(67,148,118,0.2)]",
                                  isOverdue && "from-negative to-negative/90 dark:from-negative-dark dark:to-negative-dark/90"
                                )}
                                style={{ width: `${displayProgress}%` }}
                              />
                              {displayProgress > 10 && (
                                <div className="h-full w-full absolute top-0 left-0 opacity-30">
                                  <div className="h-[1px] w-full bg-white/20 absolute top-[25%]" />
                                  <div className="h-[1px] w-full bg-white/20 absolute top-[50%]" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // No goals found
        <div className="flex flex-col items-center justify-center py-12 text-center bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-2xl">
          <div className="w-16 h-16 rounded-xl bg-secondary/10 dark:bg-primary-dark/10 flex items-center justify-center mb-4">
            <Target className="w-8 h-8 text-secondary dark:text-primary-dark" />
          </div>
          <h3 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
            No saving goals yet
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-2 max-w-md mx-auto">
            {savingsAccounts.length > 0 
              ? "Start planning your financial future by creating your first saving goal"
              : "You need to create a savings account first before adding goals"}
          </p>
          <Button
            onClick={() => savingsAccounts.length > 0 ? setIsAddDialogOpen(true) : null}
            className="mt-6 bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white transition-all duration-200 h-11 px-6 rounded-xl shadow-sm"
            disabled={savingsAccounts.length === 0}
          >
            <Plus className="w-4 h-4 mr-2" />
            {savingsAccounts.length > 0 ? "Add Goal" : "Create a Savings Account First"}
          </Button>
        </div>
      )}

      {/* Dialogs */}
      <AddSavingGoalDialog
        open={isAddDialogOpen}
        onOpenChange={(open) => {
          setIsAddDialogOpen(open)
          if (!open) handleDialogClose()
        }}
        savingsAccounts={savingsAccounts as SimplifiedAccount[]}
      />

      {editingGoal && (
        <EditSavingGoalDialog
          goal={editingGoal}
          open={!!editingGoal}
          onOpenChange={(open) => {
            if (!open) handleDialogClose()
          }}
          savingsAccounts={savingsAccounts as SimplifiedAccount[]}
        />
      )}

      {viewingTransactions && (
        <TransactionsDialog
          goal={viewingTransactions}
          open={!!viewingTransactions}
          onOpenChange={(open) => {
            if (!open) handleDialogClose()
          }}
        />
      )}

      {assigningTransactionsToGoal && (
        <AssignTransactionsDialog
          goal={assigningTransactionsToGoal}
          isOpen={!!assigningTransactionsToGoal}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
}
